/* eslint-disable @typescript-eslint/ban-ts-comment */
import React, { useState, ChangeEvent, MouseEvent, FormEvent } from 'react'
import { Button, Flex, Link, Icon } from '@ui'
import image from 'assets/logo-spaceIQ-endorsement.svg'
import { Checkbox, FormGroup, InputGroup, Classes, Intent, ICalloutProps } from '@blueprintjs/core'
import { navigate, Router, RouteComponentProps, NavigateFn, WindowLocation } from '@reach/router'
import withQuery from '@utils/withQuery'
import { PasswordChecklist } from 'components/Settings/Settings'
import { PasswordRequirement } from 'components/Settings'
import api from '@api'
import usePasswordPolicies from 'components/Settings/hooks/usePasswordPolicies'
import css from './unauthorizedApp.module.scss'

enum flow {
  forgot = 'forgot',
  sso = 'sso',
  invite = 'invite',
  passcode = 'passcode',
}

type oneInputFlows = {
  [key in flow]: {
    buttonText: string
    text: string
    successMessage?: string
  }
}

const SetPasswordScreenWithQuery = withQuery(SetPasswordScreen)

export function UnauthorizedApp() {
  return (
    <Router basepath={process.env.PUBLIC_URL}>
      <LoginScreen path="*" />
      <OneInputFlow path="forgot" currentFlow={flow.forgot} />
      <OneInputFlow path="invite" currentFlow={flow.invite} />
      <SetPasswordScreenWithQuery path="password_reset" />
      <SetPasswordScreenWithQuery path="set_password" />
    </Router>
  )
}

function LoginScreen(_: RouteComponentProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [needPasscode, setNeedpasscode] = useState(false)
  const [SSOLogin, setSSOLogin] = useState(false)

  if (needPasscode)
    return <OneInputFlow currentFlow={flow.passcode} credentials={{ email, password }} />

  if (SSOLogin) return <OneInputFlow currentFlow={flow.sso} back={() => setSSOLogin(false)} />
  const validateEmail = () => email?.includes('@eptura.com')
  const submit = (e: FormEvent) => {
    e.preventDefault()
    setLoading(true)
    api.auth
      .login({
        password,
        email,
      })
      .then((json: any) => {
        if (validateEmail()) {
          if (json.access_token) {
            window.localStorage.setItem(
              `access_token-${window.location.hostname}`,
              json.access_token
            )

            if (json.rememberDeviceToken)
              localStorage.setItem('device-token', json.rememberDeviceToken)

            navigate('floor_plan')
            window.location.reload()
          }

          if (json.error) {
            if (typeof json.error === 'string') {
              setError(json.error)
              setLoading(false)
            }

            if (typeof json.error === 'object' && json.error.meta?.needs_otp) {
              setLoading(false)
              setNeedpasscode(true)
            }
          }
        } else {
          setError('Invalid Email Id')
          setLoading(false)
        }
      })
      .catch((error) => {
        setError(error.message)
        setLoading(false)
      })
  }

  return (
    <div className={css.form_wrapper}>
      <Logo />
      <div className="form-container">
        <ErrorMessage text={error} />
        <form className="input-form" onSubmit={submit}>
          {/*         
            // @ts-ignore */}
          <InputGroup
            large
            autoFocus
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
          />
          {/*
            // @ts-ignore */}
          <InputGroup
            large
            type="password"
            placeholder="Password"
            style={{ marginTop: 15 }}
            value={password}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          />
          <Link
            data-button="forgot_password"
            className="forgot-link"
            onClick={() => navigate('/forgot')}>
            Forgot password?
          </Link>
          <Button
            large
            className="mla"
            disabled={!(password && email)}
            loading={loading}
            data-button="login"
            type="submit"
            intent={Intent.PRIMARY}
            style={{
              width: '100%',
              marginTop: 20,
            }}
            text="Login"
          />
        </form>

        <div className="separator">
          <span>Or</span>
        </div>

        <Button
          large
          ghost={false}
          style={{
            width: '100%',
            marginTop: 30,
            fontSize: '0.9rem',
          }}
          data-button="sso_login"
          onClick={() => setSSOLogin(true)}
          text="Sign in with SSO"
        />
        <div className="footer">
          {'New to SpaceIQ? '}
          <a
            data-button="invite"
            href="/invite"
            onClick={(e) => {
              e.preventDefault()
              navigate('invite')
            }}>
            Sign up
          </a>
        </div>
      </div>
    </div>
  )
}

const flows: oneInputFlows = {
  forgot: {
    buttonText: 'Reset my password',
    text: 'Enter your company email',
    successMessage: 'Password reset email sent.',
  },
  sso: {
    buttonText: 'Continue',
    text: 'Enter your company email',
  },
  invite: {
    buttonText: 'Send an invite',
    text: 'Enter your company email',
    successMessage: 'An invitation was sent to your email address.',
  },
  passcode: {
    buttonText: 'Continue',
    text: 'Check your email for a one-time passcode.',
    successMessage: 'Passcode resent',
  },
}

export function OneInputFlow({
  currentFlow,
  credentials,
  back,
}: {
  currentFlow: flow
  credentials?: { email: string; password: string }
  back?: () => void
} & RouteComponentProps) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [email, setEmail] = useState('')
  const [passcode, setPasscode] = useState('')
  const [rememberDevice, setRememberDevice] = useState(false)

  const clearState = () => {
    setError('')
    setSuccess(false)
  }

  const handleInvite = () => {
    setLoading(true)
    clearState()

    api.auth
      .invite(email)
      .then((response) => {
        if (response.error) {
          setError(response.error)
          setLoading(false)
        }

        if (!response.error) {
          setSuccess(true)
        }
      })
      .catch((error) => {
        setError(error.message)
        setLoading(false)
      })
  }

  const handleResetPassword = () => {
    setLoading(true)
    clearState()

    api.auth
      .passwordReset(email)
      .then((response) => {
        if (response.error) {
          setError(response.error)
          setLoading(false)
        }

        if (!response.error) {
          setSuccess(true)
        }
      })
      .catch((error) => {
        setError(error.message)
        setLoading(false)
      })
  }

  const handleResendCode = (e: MouseEvent) => {
    e.preventDefault()
    setLoading(true)
    clearState()

    if (credentials) {
      api.auth
        .login(credentials)
        .then((json) => {
          if (json.error.meta?.needs_otp) {
            setLoading(false)
            setSuccess(true)
          }
        })
        .catch((error) => {
          setLoading(false)
          setError(error.message)
        })
    }
  }

  const handleAuthWithOtpCode = () => {
    if (credentials) {
      setLoading(true)
      clearState()

      api.auth
        .login({
          ...credentials,
          otp_code: passcode,
          remember_device: rememberDevice,
          remember_device_token: localStorage.getItem('device-token'),
        })
        .then((json) => {
          if (json.access_token) {
            window.localStorage.setItem(
              `access_token-${window.location.hostname}`,
              json.access_token
            )

            if (json.rememberDeviceToken)
              localStorage.setItem('device-token', json.rememberDeviceToken)

            window.location.reload()
          }

          if (json.error) {
            setError(json.error)
            setLoading(false)
          }
        })
    }
  }

  const handleSSOsubmit = () => {
    if (email) {
      setLoading(true)
      clearState()

      api.auth
        .ssoLogin(email)
        .then((json) => {
          if (json.error) {
            setLoading(false)
            setError(json.error)
          }

          navigate(json.url)
        })
        .catch((error) => {
          setLoading(false)
          setError(error.message)
        })
    }
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()
    setLoading((prev) => !prev)
    if (currentFlow === flow.forgot) {
      handleResetPassword()
    }

    if (currentFlow === flow.invite) {
      handleInvite()
    }

    if (currentFlow === flow.passcode) {
      handleAuthWithOtpCode()
    }

    if (currentFlow === flow.sso) {
      handleSSOsubmit()
    }
  }

  return (
    <div className={css.form_wrapper}>
      <Logo />
      <div className="form-container">
        <ErrorMessage text={error} />
        <SuccessMessage text={success ? flows[currentFlow].successMessage : undefined} />
        <form className="input-container" onSubmit={submit}>
          {/*         
            // @ts-ignore */}
          {!success && currentFlow !== flow.passcode && (
            <FormGroup label={flows[currentFlow].text}>
              <InputGroup
                large
                autoFocus
                type="text"
                value={email}
                placeholder="Email"
                onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
              />
            </FormGroup>
          )}
          {currentFlow === flow.passcode && (
            <>
              <FormGroup label={flows[currentFlow].text}>
                <InputGroup
                  data-cy="passcode"
                  autoFocus
                  placeholder="1 2 3 4 5 6"
                  onChange={(e: ChangeEvent<HTMLInputElement>) => setPasscode(e.target.value)}
                  value={passcode}
                  autoComplete="one-time-code"
                  maxLength={6}
                  style={{
                    fontSize: 18,
                    textAlign: 'center',
                    fontFamily: 'monospace',
                  }}
                />
              </FormGroup>
              <Checkbox
                checked={rememberDevice}
                label="Remember this device"
                onChange={() => setRememberDevice((prev) => !prev)}
              />
            </>
          )}
          {(!success || currentFlow === flow.passcode) && (
            <Button
              large
              loading={loading}
              className="mla"
              data-button={flows[currentFlow].buttonText.toLowerCase()}
              intent={Intent.PRIMARY}
              disabled={!(passcode.length || email.length)}
              text={flows[currentFlow].buttonText}
              type="submit"
              style={{
                width: '100%',
                marginTop: 20,
              }}
            />
          )}
        </form>
        {success && currentFlow !== flow.passcode && (
          <Button
            large
            className="mla"
            data-button="done"
            intent={Intent.PRIMARY}
            style={{
              width: '100%',
              marginTop: 5,
            }}
            onClick={() => {
              navigate('login')
            }}
            text="Done"
          />
        )}
        {!success && currentFlow !== flow.passcode && (
          <div className="footer forgot">
            <Link
              data-button="back"
              onClick={() => {
                if (flow.sso) {
                  back && back()
                }

                navigate('login')
              }}>
              <Flex align="center">
                <Icon name="chevron-left" style={{ fontSize: 10, paddingRight: 8 }} />
                Back
              </Flex>
            </Link>
          </div>
        )}
        {currentFlow === flow.passcode && (
          <div className="footer forgot">
            <Link data-button="resend_code" onClick={handleResendCode}>
              Resend code
            </Link>
          </div>
        )}
      </div>
    </div>
  )
}

interface SetPasswordScreenProps {
  navigate: NavigateFn
  location: WindowLocation & { query: { [key: string]: string } }
}

function SetPasswordScreen({ navigate, location }: SetPasswordScreenProps) {
  const [error, setError] = useState()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')

  const policies = usePasswordPolicies()

  if (!location.query.token) {
    navigate('login')
    window.location.reload()
    return null
  }

  const submit = (e: FormEvent) => {
    e.preventDefault()

    if (success) navigate('login')

    setLoading(true)
    api.auth
      .passwordResetWithToken(policies.password, location.query.token)
      .then((response) => {
        setLoading(false)
        if (response.error) {
          setError(response.error)
        } else {
          setSuccess('Password reset email sent.')
        }
      })
      .catch((e) => {
        setLoading(false)
        setError(e.message)
      })
  }

  return (
    <div className={css.form_wrapper}>
      <Logo />
      <div className="form-container">
        <ErrorMessage text={error} />
        <SuccessMessage text={success} />
        <form className="input-container" onSubmit={submit}>
          {!success && (
            <>
              <div style={{ marginBottom: 40 }}>
                <p style={{ color: 'var(--grey1)' }}>Password requirements:</p>
                {policies.fetchingPolicies && <Skeleton />}
                {!policies.fetchingPolicies && (
                  <PasswordChecklist>
                    {policies.list.map((p, i) => (
                      <PasswordRequirement key={i} pass={p.complete}>
                        {p.message}
                      </PasswordRequirement>
                    ))}
                  </PasswordChecklist>
                )}
              </div>
              <FormGroup label="New password">
                <InputGroup
                  large
                  autoFocus
                  type="password"
                  value={policies.password}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    policies.setPassword(e.target.value)
                  }
                />
              </FormGroup>
              <FormGroup label="Confirm password">
                <InputGroup
                  large
                  type="password"
                  value={policies.confirm}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    policies.setConfirm(e.target.value)
                  }
                />
              </FormGroup>
            </>
          )}
          <Button
            large
            className="mla"
            loading={loading}
            style={{ width: '100%', marginTop: success ? 0 : 20 }}
            disabled={!(success || policies.isValid)}
            intent={Intent.PRIMARY}
            text={success ? 'Done' : 'Set password'}
            type="submit"
          />
        </form>
      </div>
    </div>
  )
}

function Logo() {
  return (
    <div className="logo-container">
      <div className={css.container}>
        <img alt="logo" src={image} />
      </div>
    </div>
  )
}

function ErrorMessage({ text }: { text?: string }) {
  if (!text) return null

  return (
    <CustomCallout intent={Intent.DANGER} faIcon="exclamation-triangle">
      <span>{text}</span>
    </CustomCallout>
  )
}

function SuccessMessage({ text }: { text?: string }) {
  if (!text) return null

  return (
    <CustomCallout intent={Intent.SUCCESS} faIcon="check-circle">
      {text}
    </CustomCallout>
  )
}

function Skeleton() {
  return (
    <div className="skeleton">
      <span className={Classes.SKELETON}>Placeholder text</span>
      <span className={Classes.SKELETON}>Placeholder text</span>
      <span className={Classes.SKELETON}>Placeholder text</span>
    </div>
  )
}

interface CalloutWithFAIconAndBackgroundProps {
  faIcon: string | null | undefined
}

function CustomCallout({ faIcon, ...props }: CalloutWithFAIconAndBackgroundProps & ICalloutProps) {
  let iconEl

  if (faIcon) {
    iconEl = (
      <div className={css.icon_wrapper}>
        <Icon name={faIcon} />
      </div>
    )
  }

  return (
    <div
      style={{
        border: 'none',
        color: props.intent === Intent.DANGER ? 'var(--red)' : 'var(--green)',
        backgroundColor: props.intent === Intent.DANGER ? 'var(--light-red)' : 'var(--light-green)',
      }}
      className={Classes.CALLOUT + ' ' + Classes.CALLOUT_ICON}>
      <div>{iconEl}</div>
      {props.children}
    </div>
  )
}
